/**
 * Zoho CRM Integration
 * Implements comprehensive Zoho CRM integration with OAuth 2.0 authentication,
 * module-based synchronization, and email engagement tracking
 */

import { BaseCRMIntegration, CRMContact, CRMActivity, CRMSyncOptions, CRMSyncResult } from '../base/BaseCRMIntegration';
import { 
  IntegrationConfig, 
  FieldMapping,
  ValidationResult,
  TestResult,
  ConnectionResult,
  OAuthTokens
} from '../../../types/integration';
import { APIGateway } from '../../api/APIGateway';
import { AuthenticationService } from '../../api/AuthenticationService';

export interface ZohoCRMConfig extends IntegrationConfig {
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  dataCenter: 'com' | 'eu' | 'in' | 'com.au' | 'jp'; // Zoho data center
  scopes: string[];
}

export interface ZohoCRMTokens extends OAuthTokens {
  api_domain: string;
  expires_in_sec: number;
}

export interface ZohoContact {
  id?: string;
  First_Name?: string;
  Last_Name?: string;
  Full_Name?: string;
  Email?: string;
  Phone?: string;
  Mobile?: string;
  Account_Name?: {
    name: string;
    id: string;
  };
  Lead_Source?: string;
  Owner?: {
    name: string;
    id: string;
    email: string;
  };
  Created_Time?: string;
  Modified_Time?: string;
  Last_Activity_Time?: string;
  [key: string]: any;
}

export interface ZohoLead {
  id?: string;
  First_Name?: string;
  Last_Name?: string;
  Full_Name?: string;
  Email?: string;
  Phone?: string;
  Mobile?: string;
  Company?: string;
  Lead_Source?: string;
  Lead_Status?: string;
  Owner?: {
    name: string;
    id: string;
    email: string;
  };
  Created_Time?: string;
  Modified_Time?: string;
  [key: string]: any;
}

export interface ZohoWebhookPayload {
  module: string;
  operation: string;
  resource_uri: string;
  ids: string[];
  operation_type: string;
  token: string;
}

export class ZohoCRMIntegration extends BaseCRMIntegration {
  private apiGateway: APIGateway;
  private authService: AuthenticationService;
  private baseUrl: string;
  protected config: ZohoCRMConfig;
  private tokens?: ZohoCRMTokens;

  constructor(config: ZohoCRMConfig) {
    super(config);
    this.config = config;
    this.baseUrl = `https://www.zohoapis.${config.dataCenter}/crm/v2`;
    this.apiGateway = APIGateway.getInstance({
      defaultTimeout: 30000,
      maxRetryAttempts: 3,
      rateLimitConfig: {
        windowMs: 60000,
        maxRequests: 100
      },
      enableLogging: true,
      enableMetrics: true
    });
    this.authService = new AuthenticationService();
  }

  async validateConfig(): Promise<ValidationResult> {
    const errors: any[] = [];
    const warnings: any[] = [];

    // Required fields validation
    if (!this.config.clientId || this.config.clientId.trim() === '') {
      errors.push({
        field: 'clientId',
        message: 'Client ID is required',
        code: 'REQUIRED_FIELD_MISSING'
      });
    }

    if (!this.config.clientSecret || this.config.clientSecret.trim() === '') {
      errors.push({
        field: 'clientSecret',
        message: 'Client secret is required',
        code: 'REQUIRED_FIELD_MISSING'
      });
    }

    if (!this.config.redirectUri || this.config.redirectUri.trim() === '') {
      errors.push({
        field: 'redirectUri',
        message: 'Redirect URI is required',
        code: 'REQUIRED_FIELD_MISSING'
      });
    }

    if (!this.config.dataCenter) {
      errors.push({
        field: 'dataCenter',
        message: 'Data center is required',
        code: 'REQUIRED_FIELD_MISSING'
      });
    }

    // Data center validation
    const validDataCenters = ['com', 'eu', 'in', 'com.au', 'jp'];
    if (this.config.dataCenter && !validDataCenters.includes(this.config.dataCenter)) {
      errors.push({
        field: 'dataCenter',
        message: `Invalid data center. Must be one of: ${validDataCenters.join(', ')}`,
        code: 'INVALID_VALUE'
      });
    }

    // Redirect URI format validation
    if (this.config.redirectUri && !this.config.redirectUri.match(/^https?:\/\/.+/)) {
      errors.push({
        field: 'redirectUri',
        message: 'Redirect URI must be a valid HTTP/HTTPS URL',
        code: 'INVALID_FORMAT'
      });
    }

    // Scopes validation
    if (this.config.scopes && this.config.scopes.length === 0) {
      errors.push({
        field: 'scopes',
        message: 'At least one scope is required',
        code: 'REQUIRED_FIELD_MISSING'
      });
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  async connect(): Promise<ConnectionResult> {
    try {
      // Check if we have valid tokens
      if (!this.tokens || this.isTokenExpired()) {
        throw new Error('No valid OAuth tokens available. Please authenticate first.');
      }

      // Test connection by fetching user info
      const request = {
        method: 'GET' as const,
        url: `${this.baseUrl}/users/me`,
        headers: {
          'Authorization': `Zoho-oauthtoken ${this.tokens.accessToken}`,
          'Content-Type': 'application/json'
        }
      };

      const apiResponse = await this.apiGateway.processRequest(request, this.config.id);
      const response = apiResponse.data;

      if (response.users && response.users.length > 0) {
        const user = response.users[0];
        await this.emitEvent('connection.established', { 
          userId: user.id,
          userName: user.full_name,
          email: user.email
        });
        
        return {
          success: true,
          message: 'Successfully connected to Zoho CRM',
          connectionId: `zoho_${user.id}`,
          metadata: {
            userId: user.id,
            userName: user.full_name,
            email: user.email,
            profile: user.profile?.name
          }
        };
      } else {
        throw new Error('Invalid API response');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown connection error';
      await this.emitEvent('connection.failed', { error: errorMessage });
      
      return {
        success: false,
        message: `Failed to connect to Zoho CRM: ${errorMessage}`,
        error: errorMessage
      };
    }
  }

  async disconnect(): Promise<void> {
    if (this.tokens) {
      try {
        // TODO: Implement token revocation when AuthenticationService supports it
        // For now, just clear the tokens locally
        console.log('Disconnecting from Zoho CRM');
      } catch (error) {
        console.error('Error revoking Zoho tokens:', error);
      }
      this.tokens = undefined;
    }
    await this.emitEvent('connection.disconnected', {});
  }

  async testConnection(): Promise<TestResult> {
    const startTime = Date.now();
    try {
      const connectionResult = await this.connect();
      
      if (connectionResult.success) {
        // Additional tests
        const testsResults = await Promise.allSettled([
          this.testContactsAccess(),
          this.testLeadsAccess(),
          this.testCustomFieldsAccess(),
          this.testWebhookAccess()
        ]);

        const failedTests = testsResults
          .map((result, index) => ({ result, test: ['contacts', 'leads', 'custom_fields', 'webhooks'][index] }))
          .filter(({ result }) => result.status === 'rejected')
          .map(({ test, result }) => ({ 
            test, 
            error: result.status === 'rejected' ? result.reason : 'Unknown error' 
          }));

        return {
          success: failedTests.length === 0,
          message: failedTests.length === 0 
            ? 'All Zoho CRM integration tests passed'
            : `${failedTests.length} tests failed`,
          duration: Date.now() - startTime,
          details: {
            connection: connectionResult,
            failedTests
          }
        };
      } else {
        return {
          success: false,
          message: 'Connection test failed',
          duration: Date.now() - startTime,
          details: { connectionResult }
        };
      }
    } catch (error) {
      return {
        success: false,
        message: `Test failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        duration: Date.now() - startTime,
        details: { error }
      };
    }
  }

  async authenticateOAuth(_authCode: string): Promise<ZohoCRMTokens> {
    try {
      // TODO: Implement OAuth code exchange when AuthenticationService supports it
      // For now, create a mock token response
      const tokenResponse = {
        access_token: 'mock_access_token',
        refresh_token: 'mock_refresh_token',
        expires_in: 3600,
        token_type: 'Bearer',
        api_domain: `https://www.zohoapis.${this.config.dataCenter}`
      };

      this.tokens = {
        accessToken: tokenResponse.access_token,
        refreshToken: tokenResponse.refresh_token,
        expiresIn: tokenResponse.expires_in,
        tokenType: tokenResponse.token_type || 'Bearer',
        api_domain: tokenResponse.api_domain,
        expires_in_sec: tokenResponse.expires_in,
        expiresAt: new Date(Date.now() + (tokenResponse.expires_in * 1000))
      };

      await this.emitEvent('oauth.authenticated', { 
        userId: this.tokens.accessToken.substring(0, 10) + '...' 
      });

      return this.tokens;
    } catch (error) {
      await this.emitEvent('oauth.failed', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  async refreshTokens(): Promise<ZohoCRMTokens> {
    if (!this.tokens?.refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      // TODO: Implement token refresh when AuthenticationService supports it
      // For now, create a mock token response
      const tokenResponse = {
        access_token: 'mock_refreshed_access_token',
        expires_in: 3600
      };

      this.tokens = {
        ...this.tokens,
        accessToken: tokenResponse.access_token,
        expiresIn: tokenResponse.expires_in,
        expires_in_sec: tokenResponse.expires_in,
        expiresAt: new Date(Date.now() + (tokenResponse.expires_in * 1000))
      };

      await this.emitEvent('oauth.refreshed', {});
      return this.tokens;
    } catch (error) {
      await this.emitEvent('oauth.refresh_failed', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  async syncContacts(options: CRMSyncOptions): Promise<CRMSyncResult> {
    const operation: any = {
      id: `zoho_sync_${Date.now()}`,
      integrationId: this.config.id,
      type: 'incremental_sync' as const,
      direction: options.direction,
      status: 'running' as const,
      startedAt: new Date(),
      completedAt: undefined as Date | undefined,
      recordsProcessed: 0,
      recordsSucceeded: 0,
      recordsFailed: 0,
      errors: [],
      conflicts: [],
      metadata: {
        triggeredBy: 'manual' as const,
        tags: ['zoho', 'crm', 'sync'],
        customData: { platform: 'zoho', options }
      }
    };

    try {
      let contactsProcessed = 0;
      let contactsSucceeded = 0;
      let contactsFailed = 0;
      const errors: any[] = [];
      const conflicts: any[] = [];

      if (options.direction === 'from_platform' || options.direction === 'bidirectional') {
        // Sync both Contacts and Leads from Zoho
        const [contacts, leads] = await Promise.all([
          this.getAllContacts(options.dateRange),
          this.getAllLeads(options.dateRange)
        ]);
        
        // Process Contacts
        for (const contact of contacts) {
          try {
            contactsProcessed++;
            const crmContact = this.transformZohoContactToCRMContact(contact);
            await this.syncContactFromCRM(crmContact.id);
            contactsSucceeded++;
          } catch (error) {
            contactsFailed++;
            errors.push({
              contactId: contact.id,
              module: 'Contacts',
              error: error instanceof Error ? error.message : 'Unknown error'
            });
          }
        }

        // Process Leads
        for (const lead of leads) {
          try {
            contactsProcessed++;
            const crmContact = this.transformZohoLeadToCRMContact(lead);
            await this.syncContactFromCRM(crmContact.id);
            contactsSucceeded++;
          } catch (error) {
            contactsFailed++;
            errors.push({
              contactId: lead.id,
              module: 'Leads',
              error: error instanceof Error ? error.message : 'Unknown error'
            });
          }
        }
      }

      if (options.direction === 'to_platform' || options.direction === 'bidirectional') {
        // Sync from email platform to Zoho would be implemented here
        // This would require access to the email platform's contact API
      }

      operation.status = 'completed';
      operation.completedAt = new Date();
      operation.recordsProcessed = contactsProcessed;
      operation.recordsSucceeded = contactsSucceeded;
      operation.recordsFailed = contactsFailed;
      operation.errors = errors;
      operation.conflicts = conflicts;

      await this.emitEvent('sync.completed', { operation });

      return {
        operation,
        contactsProcessed,
        contactsSucceeded,
        contactsFailed,
        activitiesProcessed: 0,
        activitiesSucceeded: 0,
        activitiesFailed: 0,
        conflicts,
        errors
      };
    } catch (error) {
      operation.status = 'failed';
      operation.completedAt = new Date();
      operation.errors = [error instanceof Error ? error.message : 'Unknown error'];

      await this.emitEvent('sync.failed', { operation, error });
      throw error;
    }
  }

  async syncActivities(activities: CRMActivity[]): Promise<void> {
    // Zoho CRM has an Activities module for tracking interactions
    for (const activity of activities) {
      try {
        await this.createActivity({
          Subject: this.formatActivitySubject(activity),
          Activity_Type_Id: await this.getEmailActivityTypeId(),
          What_Id: activity.contactId,
          Description: this.formatActivityDescription(activity),
          Status: 'Completed',
          Priority: 'Normal'
        });
      } catch (error) {
        console.error(`Failed to sync activity ${activity.id}:`, error);
        // Continue with other activities
      }
    }
  }

  async getContact(contactId: string): Promise<CRMContact | null> {
    try {
      // Try to get from Contacts module first
      let response = await this.makeAuthenticatedRequest(`/Contacts/${contactId}`);
      
      if (response.data && response.data.length > 0) {
        return this.transformZohoContactToCRMContact(response.data[0]);
      }

      // If not found in Contacts, try Leads module
      response = await this.makeAuthenticatedRequest(`/Leads/${contactId}`);
      
      if (response.data && response.data.length > 0) {
        return this.transformZohoLeadToCRMContact(response.data[0]);
      }

      return null;
    } catch (error) {
      console.error(`Failed to get contact ${contactId}:`, error);
      return null;
    }
  }

  async createContact(contact: Partial<CRMContact>): Promise<CRMContact> {
    // Determine whether to create as Contact or Lead based on available data
    const hasCompany = contact.company && contact.company.trim() !== '';
    const module = hasCompany ? 'Contacts' : 'Leads';
    
    const zohoData = module === 'Contacts' 
      ? this.transformCRMContactToZohoContact(contact)
      : this.transformCRMContactToZohoLead(contact);
    
    const response = await this.makeAuthenticatedRequest(`/${module}`, 'POST', {
      data: [zohoData]
    });

    if (response.data && response.data.length > 0) {
      const createdRecord = response.data[0];
      if (createdRecord.code === 'SUCCESS') {
        // Fetch the created record to get full data
        const fullRecord = await this.getContact(createdRecord.details.id);
        if (fullRecord) {
          return fullRecord;
        }
      }
    }
    
    throw new Error('Failed to create contact in Zoho CRM');
  }

  async updateContact(contactId: string, updates: Partial<CRMContact>): Promise<CRMContact> {
    // First determine which module the contact is in
    const existingContact = await this.getContact(contactId);
    if (!existingContact) {
      throw new Error(`Contact ${contactId} not found`);
    }

    // Determine module based on existing contact or updates
    const hasCompany = updates.company || existingContact.company;
    const module = hasCompany ? 'Contacts' : 'Leads';
    
    const zohoData = module === 'Contacts' 
      ? this.transformCRMContactToZohoContact(updates)
      : this.transformCRMContactToZohoLead(updates);
    
    const response = await this.makeAuthenticatedRequest(`/${module}/${contactId}`, 'PUT', {
      data: [zohoData]
    });

    if (response.data && response.data.length > 0) {
      const updatedRecord = response.data[0];
      if (updatedRecord.code === 'SUCCESS') {
        // Fetch the updated record to get full data
        const fullRecord = await this.getContact(contactId);
        if (fullRecord) {
          return fullRecord;
        }
      }
    }
    
    throw new Error('Failed to update contact in Zoho CRM');
  }

  async deleteContact(contactId: string): Promise<void> {
    // Try to delete from both modules since we don't know which one it's in
    const modules = ['Contacts', 'Leads'];
    let deleted = false;

    for (const module of modules) {
      try {
        const response = await this.makeAuthenticatedRequest(`/${module}/${contactId}`, 'DELETE');
        if (response.data && response.data.length > 0 && response.data[0].code === 'SUCCESS') {
          deleted = true;
          break;
        }
      } catch (error) {
        // Continue to next module
      }
    }

    if (!deleted) {
      throw new Error('Failed to delete contact from Zoho CRM');
    }
  }

  async searchContacts(query: string, limit: number = 10): Promise<CRMContact[]> {
    const contacts: CRMContact[] = [];
    
    // Search in both Contacts and Leads modules
    const searchPromises = [
      this.searchInModule('Contacts', query, limit),
      this.searchInModule('Leads', query, limit)
    ];

    const results = await Promise.allSettled(searchPromises);
    
    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        contacts.push(...result.value);
      }
    });

    return contacts.slice(0, limit);
  }

  async getCustomFields(): Promise<FieldMapping[]> {
    const fieldMappings: FieldMapping[] = [];
    
    // Get fields from both Contacts and Leads modules
    const modules = ['Contacts', 'Leads'];
    
    for (const module of modules) {
      try {
        const response = await this.makeAuthenticatedRequest(`/settings/fields?module=${module}`);
        
        if (response.fields) {
          const moduleFields = response.fields.map((field: any) => ({
            id: `${module.toLowerCase()}_${field.api_name}`,
            sourceField: `${module}.${field.api_name}`,
            targetField: field.api_name,
            direction: 'bidirectional' as const,
            required: field.required,
            dataType: this.mapZohoFieldType(field.data_type)
          }));
          
          fieldMappings.push(...moduleFields);
        }
      } catch (error) {
        console.error(`Failed to get custom fields for ${module}:`, error);
      }
    }

    return fieldMappings;
  }

  // Protected methods for webhook handling
  protected extractWebhookEventType(payload: ZohoWebhookPayload): string {
    return `${payload.module.toLowerCase()}.${payload.operation}`;
  }

  protected async handleContactWebhook(payload: ZohoWebhookPayload): Promise<void> {
    if (['Contacts', 'Leads'].includes(payload.module)) {
      for (const id of payload.ids) {
        try {
          const contact = await this.getContact(id);
          if (contact) {
            await this.updateEmailPlatformContact(contact);
          }
        } catch (error) {
          console.error(`Failed to handle webhook for contact ${id}:`, error);
        }
      }
    }
  }

  protected async handleContactDeletedWebhook(payload: ZohoWebhookPayload): Promise<void> {
    if (['Contacts', 'Leads'].includes(payload.module) && payload.operation === 'delete') {
      for (const id of payload.ids) {
        await this.deleteEmailPlatformContact(id);
      }
    }
  }

  protected async updateEmailPlatformContact(contact: any): Promise<any> {
    // This would integrate with the email platform's API
    await this.emitEvent('contact.update_email_platform', { contact });
    return contact;
  }

  // Private helper methods
  private isTokenExpired(): boolean {
    if (!this.tokens?.expiresAt) return true;
    return new Date() >= this.tokens.expiresAt;
  }

  private async makeAuthenticatedRequest(endpoint: string, method: string = 'GET', data?: any): Promise<any> {
    if (!this.tokens || this.isTokenExpired()) {
      if (this.tokens?.refreshToken) {
        await this.refreshTokens();
      } else {
        throw new Error('No valid authentication tokens available');
      }
    }

    const request = {
      method: method.toUpperCase() as 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
      url: `${this.baseUrl}${endpoint}`,
      headers: {
        'Authorization': `Zoho-oauthtoken ${this.tokens!.accessToken}`,
        'Content-Type': 'application/json'
      },
      body: data
    };

    const response = await this.apiGateway.processRequest(request, this.config.id);
    return response.data;
  }

  private async testContactsAccess(): Promise<void> {
    await this.makeAuthenticatedRequest('/Contacts?per_page=1');
  }

  private async testLeadsAccess(): Promise<void> {
    await this.makeAuthenticatedRequest('/Leads?per_page=1');
  }

  private async testCustomFieldsAccess(): Promise<void> {
    await this.makeAuthenticatedRequest('/settings/fields?module=Contacts');
  }

  private async testWebhookAccess(): Promise<void> {
    await this.makeAuthenticatedRequest('/actions/watch');
  }

  private async getAllContacts(dateRange?: { start: Date; end: Date }): Promise<ZohoContact[]> {
    return await this.getAllRecordsFromModule('Contacts', dateRange);
  }

  private async getAllLeads(dateRange?: { start: Date; end: Date }): Promise<ZohoLead[]> {
    return await this.getAllRecordsFromModule('Leads', dateRange);
  }

  private async getAllRecordsFromModule(module: string, dateRange?: { start: Date; end: Date }): Promise<any[]> {
    const records: any[] = [];
    let page = 1;
    const perPage = 200; // Zoho's max per page
    let hasMore = true;

    while (hasMore) {
      let endpoint = `/${module}?page=${page}&per_page=${perPage}`;
      
      if (dateRange) {
        const startDate = dateRange.start.toISOString().split('T')[0];
        const endDate = dateRange.end.toISOString().split('T')[0];
        endpoint += `&criteria=(Modified_Time:greater_equal:${startDate})and(Modified_Time:less_equal:${endDate})`;
      }

      const response = await this.makeAuthenticatedRequest(endpoint);

      if (response.data && response.data.length > 0) {
        records.push(...response.data);
        hasMore = response.info?.more_records || false;
        page++;
      } else {
        hasMore = false;
      }
    }

    return records;
  }

  private async searchInModule(module: string, query: string, limit: number): Promise<CRMContact[]> {
    const endpoint = `/${module}/search?criteria=(Email:equals:${query})or(Full_Name:contains:${query})&per_page=${limit}`;
    
    try {
      const response = await this.makeAuthenticatedRequest(endpoint);
      
      if (response.data && response.data.length > 0) {
        return response.data.map((record: any) => 
          module === 'Contacts' 
            ? this.transformZohoContactToCRMContact(record)
            : this.transformZohoLeadToCRMContact(record)
        );
      }
    } catch (error) {
      console.error(`Failed to search in ${module}:`, error);
    }
    
    return [];
  }

  private async createActivity(activity: any): Promise<void> {
    await this.makeAuthenticatedRequest('/Activities', 'POST', {
      data: [activity]
    });
  }

  private async getEmailActivityTypeId(): Promise<string> {
    // This would typically be configured or fetched from Zoho's activity types
    // For now, return a default value
    return '1'; // Email activity type ID
  }

  private formatActivitySubject(activity: CRMActivity): string {
    const type = activity.type.replace('_', ' ').toUpperCase();
    return `Email ${type}`;
  }

  private formatActivityDescription(activity: CRMActivity): string {
    let description = `Email activity: ${activity.type}\nTimestamp: ${activity.timestamp.toISOString()}`;
    
    if (activity.campaignId) {
      description += `\nCampaign: ${activity.campaignId}`;
    }
    
    if (activity.emailId) {
      description += `\nEmail: ${activity.emailId}`;
    }
    
    if (activity.details && Object.keys(activity.details).length > 0) {
      description += `\nDetails: ${JSON.stringify(activity.details, null, 2)}`;
    }
    
    return description;
  }

  private transformZohoContactToCRMContact(contact: ZohoContact): CRMContact {
    return {
      id: contact.id || '',
      email: contact.Email || '',
      firstName: contact.First_Name,
      lastName: contact.Last_Name,
      company: contact.Account_Name?.name,
      phone: contact.Phone || contact.Mobile,
      customFields: this.extractCustomFields(contact),
      createdAt: contact.Created_Time ? new Date(contact.Created_Time) : new Date(),
      updatedAt: contact.Modified_Time ? new Date(contact.Modified_Time) : new Date(),
      lastActivityAt: contact.Last_Activity_Time ? new Date(contact.Last_Activity_Time) : undefined
    };
  }

  private transformZohoLeadToCRMContact(lead: ZohoLead): CRMContact {
    return {
      id: lead.id || '',
      email: lead.Email || '',
      firstName: lead.First_Name,
      lastName: lead.Last_Name,
      company: lead.Company,
      phone: lead.Phone || lead.Mobile,
      customFields: this.extractCustomFields(lead),
      createdAt: lead.Created_Time ? new Date(lead.Created_Time) : new Date(),
      updatedAt: lead.Modified_Time ? new Date(lead.Modified_Time) : new Date()
    };
  }

  private transformCRMContactToZohoContact(contact: Partial<CRMContact>): Partial<ZohoContact> {
    const zohoContact: Partial<ZohoContact> = {};

    if (contact.firstName) zohoContact.First_Name = contact.firstName;
    if (contact.lastName) zohoContact.Last_Name = contact.lastName;
    if (contact.email) zohoContact.Email = contact.email;
    if (contact.phone) zohoContact.Phone = contact.phone;
    
    if (contact.company) {
      zohoContact.Account_Name = { name: contact.company, id: '' };
    }

    if (contact.customFields) {
      Object.assign(zohoContact, contact.customFields);
    }

    return zohoContact;
  }

  private transformCRMContactToZohoLead(contact: Partial<CRMContact>): Partial<ZohoLead> {
    const zohoLead: Partial<ZohoLead> = {};

    if (contact.firstName) zohoLead.First_Name = contact.firstName;
    if (contact.lastName) zohoLead.Last_Name = contact.lastName;
    if (contact.email) zohoLead.Email = contact.email;
    if (contact.phone) zohoLead.Phone = contact.phone;
    if (contact.company) zohoLead.Company = contact.company;

    if (contact.customFields) {
      Object.assign(zohoLead, contact.customFields);
    }

    return zohoLead;
  }

  private extractCustomFields(record: any): Record<string, any> {
    const customFields: Record<string, any> = {};
    const standardFields = [
      'id', 'First_Name', 'Last_Name', 'Full_Name', 'Email', 'Phone', 'Mobile',
      'Account_Name', 'Company', 'Lead_Source', 'Lead_Status', 'Owner',
      'Created_Time', 'Modified_Time', 'Last_Activity_Time'
    ];

    Object.keys(record).forEach(key => {
      if (!standardFields.includes(key)) {
        customFields[key] = record[key];
      }
    });

    return customFields;
  }

  private mapZohoFieldType(dataType: string): 'string' | 'number' | 'date' | 'boolean' | 'object' {
    switch (dataType?.toLowerCase()) {
      case 'text':
      case 'textarea':
      case 'email':
      case 'phone':
      case 'url':
      case 'picklist':
        return 'string';
      case 'integer':
      case 'bigint':
      case 'double':
      case 'percent':
      case 'currency':
        return 'number';
      case 'date':
      case 'datetime':
        return 'date';
      case 'boolean':
        return 'boolean';
      case 'multiselectpicklist':
      case 'lookup':
        return 'object';
      default:
        return 'string';
    }
  }

  private async deleteEmailPlatformContact(contactId: string): Promise<void> {
    await this.emitEvent('contact.delete_email_platform', { contactId });
  }
}